import {
  createRootRoute,
  createRoute,
  lazyRouteComponent,
} from '@tanstack/react-router'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { AuthGuard } from '@/components/auth/AuthGuard'

// Eager: auth callback (needs to run before guard)
import { AuthCallbackPage } from '@/pages/AuthCallback'

// Lazy: all admin pages
const DashboardPage = lazyRouteComponent(() => import('@/pages/Dashboard'), 'DashboardPage')
const TenantsPage = lazyRouteComponent(() => import('@/pages/Tenants'), 'TenantsPage')
const TenantDetailPage = lazyRouteComponent(() => import('@/pages/TenantDetail'), 'TenantDetailPage')
const SubscriptionsPage = lazyRouteComponent(() => import('@/pages/Subscriptions'), 'SubscriptionsPage')
const CatalogPage = lazyRouteComponent(() => import('@/pages/Catalog'), 'CatalogPage')
const ProductDetailPage = lazyRouteComponent(() => import('@/pages/ProductDetail'), 'ProductDetailPage')
const OrdersPage = lazyRouteComponent(() => import('@/pages/Orders'), 'OrdersPage')
const InvoicesPage = lazyRouteComponent(() => import('@/pages/Invoices'), 'InvoicesPage')
const PaymentsPage = lazyRouteComponent(() => import('@/pages/Payments'), 'PaymentsPage')
const SecurityPage = lazyRouteComponent(() => import('@/pages/Security'), 'SecurityPage')
const BackupsPage = lazyRouteComponent(() => import('@/pages/Backups'), 'BackupsPage')
const AosOverviewPage = lazyRouteComponent(() => import('@/pages/AosOverview'), 'AosOverviewPage')
const AosEventsPage = lazyRouteComponent(() => import('@/pages/AosEvents'), 'AosEventsPage')
const AosTriggersPage = lazyRouteComponent(() => import('@/pages/AosTriggers'), 'AosTriggersPage')
const IntegrationsPage = lazyRouteComponent(() => import('@/pages/Integrations'), 'IntegrationsPage')
const SettingsPage = lazyRouteComponent(() => import('@/pages/Settings'), 'SettingsPage')
const ActivityHistoryPage = lazyRouteComponent(() => import('@/pages/ActivityHistory'), 'ActivityHistoryPage')
const ReconciliationPage = lazyRouteComponent(() => import('@/pages/Reconciliation'), 'ReconciliationPage')
const RenewalsPage = lazyRouteComponent(() => import('@/pages/Renewals'), 'RenewalsPage')
const AppxiteCustomersPage = lazyRouteComponent(() => import('@/pages/AppxiteCustomers'), 'AppxiteCustomersPage')
const AppxiteOrdersPage = lazyRouteComponent(() => import('@/pages/AppxiteOrders'), 'AppxiteOrdersPage')
const AppxiteSubscriptionsPage = lazyRouteComponent(() => import('@/pages/AppxiteSubscriptions'), 'AppxiteSubscriptionsPage')

/**
 * Root layout: AuthGuard wraps AdminLayout.
 * Every route is protected — unauthenticated users see login,
 * non-staff users see access denied.
 */
function RootComponent() {
  return (
    <AuthGuard>
      <AdminLayout />
    </AuthGuard>
  )
}

const rootRoute = createRootRoute({
  component: RootComponent,
})

// Auth callback (handled before guard redirects)
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackPage,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
})

const tenantsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants',
  component: TenantsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    search: (search.search as string) || '',
    status: (search.status as string) || '',
    page: Number(search.page) || 1,
  }),
})

const tenantDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/tenants/$tenantId',
  component: TenantDetailPage,
})

const subscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/subscriptions',
  component: SubscriptionsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: (search.status as string) || 'active',
    page: Number(search.page) || 1,
  }),
})

const catalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog',
  component: CatalogPage,
})

const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catalog/$productId',
  component: ProductDetailPage,
})

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  component: OrdersPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: (search.status as string) || '',
    page: Number(search.page) || 1,
  }),
})

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/invoices',
  component: InvoicesPage,
})

const paymentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing/payments',
  component: PaymentsPage,
})

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  component: SecurityPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: (search.status as string) || 'open',
    severity: (search.severity as string) || '',
    page: Number(search.page) || 1,
  }),
})

const backupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backups',
  component: BackupsPage,
})

const aosOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations',
  component: AosOverviewPage,
})

const aosProposalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations/proposals',
  component: AosOverviewPage,
})

const aosMissionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations/missions',
  component: AosOverviewPage,
})

const aosTriggersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations/triggers',
  component: AosTriggersPage,
})

const aosEventsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/operations/events',
  component: AosEventsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    severity: (search.severity as string) || '',
    page: Number(search.page) || 1,
  }),
})

const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/integrations',
  component: IntegrationsPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
})

const activityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activity',
  component: ActivityHistoryPage,
})

const reconciliationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reconciliation',
  component: ReconciliationPage,
})

const renewalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/renewals',
  component: RenewalsPage,
})

const appxiteCustomersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appxite/customers',
  component: AppxiteCustomersPage,
  validateSearch: (search: Record<string, unknown>) => ({
    search: (search.search as string) || '',
    status: (search.status as string) || '',
    country: (search.country as string) || '',
    page: Number(search.page) || 1,
  }),
})

const appxiteOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appxite/orders',
  component: AppxiteOrdersPage,
  validateSearch: (search: Record<string, unknown>) => ({
    search: (search.search as string) || '',
    status_name: (search.status_name as string) || '',
    page: Number(search.page) || 1,
  }),
})

const appxiteSubscriptionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appxite/subscriptions',
  component: AppxiteSubscriptionsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    search: (search.search as string) || '',
    status: (search.status as string) || '',
    vendor_name: (search.vendor_name as string) || '',
    customer_name: (search.customer_name as string) || '',
    page: Number(search.page) || 1,
  }),
})

export const routeTree = rootRoute.addChildren([
  authCallbackRoute,
  indexRoute,
  tenantsRoute,
  tenantDetailRoute,
  subscriptionsRoute,
  catalogRoute,
  productDetailRoute,
  ordersRoute,
  invoicesRoute,
  paymentsRoute,
  securityRoute,
  backupsRoute,
  aosOverviewRoute,
  aosProposalsRoute,
  aosMissionsRoute,
  aosTriggersRoute,
  aosEventsRoute,
  integrationsRoute,
  settingsRoute,
  activityRoute,
  reconciliationRoute,
  renewalsRoute,
  appxiteCustomersRoute,
  appxiteOrdersRoute,
  appxiteSubscriptionsRoute,
])
